import { apiGet, apiPost } from './client';
import type { OperationResult } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Instruction history — versioned audit trail of agent instruction edits.
//
// Backed by ai.AgentInstructionHistory. Each row holds the body that WAS
// REPLACED on a save — the current body always lives on ai.AiAgent.Instructions.
// The drawer / diff view loads list rows (no body) up-front, then fetches one
// detail at a time for diff rendering.
//
// All routes are merchant-scoped server-side via GetTenantManager().SelectedMerchantId.
// ─────────────────────────────────────────────────────────────────────────────

/** Summary row — `instructions` body is intentionally NOT included to keep the list payload small. */
export interface InstructionHistoryRow {
  id: number;
  aiAgentId: number;
  /** ISO string. */
  changedDate: string;
  /** TenantManager.Id of the editor. */
  changedBy: number;
  /** DisplayName / FirstName+LastName / Email — null if user no longer exists. */
  changedByName?: string | null;
  charCount: number;
  instructionsHash: string;
}

/** Detail row — same as summary plus the full body for diff rendering. */
export interface InstructionHistoryDetail extends InstructionHistoryRow {
  instructions: string;
}

export interface InstructionHistoryPage {
  rowCount: number;
  pageNo: number;
  pageSize: number;
  data: InstructionHistoryRow[];
}

export async function listInstructionHistory(
  agentId: number,
  pageNo = 1,
  pageSize = 50,
): Promise<InstructionHistoryPage> {
  const wrapped = await apiGet<OperationResult<InstructionHistoryPage>>(
    `/api/Agent/InstructionHistory/${agentId}`,
    { pageNo, pageSize },
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'We couldn’t load the version history. Please try again in a moment.');
  }
  return wrapped.data;
}

export async function getInstructionHistoryDetail(
  agentId: number,
  historyId: number,
): Promise<InstructionHistoryDetail> {
  const wrapped = await apiGet<OperationResult<InstructionHistoryDetail>>(
    `/api/Agent/InstructionHistory/${agentId}/${historyId}`,
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'We couldn’t load that version.');
  }
  return wrapped.data;
}

export interface RestoreInstructionResponse {
  agentId: number;
  instructions: string;
  updatedDate: string;
}

export async function restoreInstructionHistory(
  agentId: number,
  historyId: number,
): Promise<RestoreInstructionResponse> {
  const wrapped = await apiPost<OperationResult<RestoreInstructionResponse>>(
    `/api/Agent/InstructionHistory/${agentId}/${historyId}/Restore`,
    {},
  );
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'We couldn’t switch to that version. Please try again.');
  }
  return wrapped.data;
}
